'use client';

import dynamic from 'next/dynamic';
import { useSelector } from 'react-redux';
import Conditional from '@next-app/components/Conditional';
import Link from '@next-app/components/elements/Link/Link';
import LLHeading from '@next-app/components/elements/LLHeading/LLHeading';
import SkeletonUI from '@next-app/components/Skeleton/Skeleton';
import {
  selectIsCheckoutDetailsLoaded,
  selectOrderSummaryInfo,
  selectWebSubTotalInfo,
  selectEmailDeliverySubtotalInfo,
  selectInstorePickupSubTotalInfo,
  selectIsPureBopisOrder,
  selectIsPureDigitalOrder,
  selectIsPureShipTypeOrder,
  selectPaymentDetailInfo,
  selectCartSummary,
} from '@next-app/lib/features/checkout/CheckoutSelector';
import {
  getFormattedPrice,
  isNotEmptyObject,
  isNullOrUndefined,
  grandTotalPFormatted,
} from '@next-app/utils/common-utils';
import LLPopover from '@next-app/components/elements/LLPopover/LLPopover';
import { selectFeatureToggles } from '@next-app/lib/features/InitSelectors';
const TaxExempt = dynamic(() => import('../TaxExempt/TaxExempt'), {
  ssr: true,
});

interface OrderSummaryProps {
  showHeading: boolean;
}

const OrderSummary = ({
  showHeading,
}: OrderSummaryProps): React.JSX.Element => {
  // selectors
  const isCheckoutDetailsLoaded = useSelector(selectIsCheckoutDetailsLoaded);
  const isPureShipOrder = useSelector(selectIsPureShipTypeOrder);
  const isPureDigitalOrder = useSelector(selectIsPureDigitalOrder);
  const isPureBopisOrder = useSelector(selectIsPureBopisOrder);
  const orderSummaryInfo = useSelector(selectOrderSummaryInfo);
  const webSubTotalInfo = useSelector(selectWebSubTotalInfo);
  const emailDeliverySubtotalInfo = useSelector(
    selectEmailDeliverySubtotalInfo,
  );
  const instorePickupSubTotalInfo = useSelector(
    selectInstorePickupSubTotalInfo,
  );
  const isPureOrder = isPureDigitalOrder || isPureBopisOrder || isPureShipOrder;
  const { taxExemptOpted = false } = useSelector(selectPaymentDetailInfo) || {};
  const cartSummary = useSelector(selectCartSummary);
  const featureToggles = useSelector(selectFeatureToggles);
  const { enableTariffSurcharge = false } = featureToggles || {};

  // methods
  const showPrice = (amount: string | number): string => {
    if (isNullOrUndefined(amount)) return '';

    if (typeof amount === 'number') {
      return getFormattedPrice(amount);
    } else {
      return amount;
    }
  };

  // Renderers
  /**
   * RenderOrderSummaryTitle
   * @param title
   */
  const RenderOrderSummaryTitle = ({ title }: { title: string }) => {
    return <div className="fw-bold mb-ll-3 mt-ll-3">{title}</div>;
  };

  /**
   * RenderOrderSummaryLineItem
   * @param params - label, amount, className
   */
  const RenderOrderSummaryLineItem = ({
    label,
    amount,
    className,
    addNegative,
    labelClassName,
    popoverText = '',
    showPopover = false,
  }: {
    label: string;
    amount: number | string;
    className?: string;
    labelClassName?: string;
    addNegative?: boolean;
    popoverText?: string;
    showPopover?: boolean;
  }) => {
    return (
      <div className={`d-flex justify-content-between my-ll-3`}>
        <div
          className={`d-flex align-items-center gap-ll-5-px ${labelClassName}`}
        >
          {label}
          <Conditional if={showPopover}>
            <LLPopover text={popoverText} position="top">
              <span
                style={{
                  width: '12px',
                  height: '12px',
                  fontSize: '12px',
                  lineHeight: '12px',
                }}
                className="ls-icon d-block icn-help-otl"
              ></span>
            </LLPopover>
          </Conditional>
        </div>

        <div className={`${className ? className : ''}`}>
          {addNegative ? '-' : ''}
          {showPrice(amount)}
        </div>
      </div>
    );
  };

  /**
   * RenderShipOrderSummary
   */

  const RenderShipOrderSummary = () => {
    const { amount, tax, orderTotal } = webSubTotalInfo;
    return (
      <div className="border-top mt-ll-3">
        <RenderOrderSummaryTitle title="Ship Order" />
        <RenderOrderSummaryLineItem label="Subtotal" amount={amount} />
        <Conditional if={enableTariffSurcharge}>
          <RenderOrderSummaryLineItem
            label="Tariff Recovery Surcharge"
            amount={grandTotalPFormatted(
              orderSummaryInfo?.webSubTotalInfo?.tariffAmount ?? 0,
            )}
            showPopover={true}
            popoverText={orderSummaryInfo?.tariffHelperText}
          />
        </Conditional>

        <RenderOrderSummaryLineItem
          label="Shipping"
          amount={grandTotalPFormatted(orderSummaryInfo?.orderShippingCharge)}
          className={`${orderSummaryInfo?.orderShippingCharge === 'tbd' || orderSummaryInfo?.orderShippingCharge === 'quote to follow' ? 'fs-sm lh-sm fst-italic' : ''}`}
        />

        <RenderOrderSummaryLineItem
          label="Taxes"
          amount={orderSummaryInfo?.orderShippingCharge === 'tbd' ? 'tbd' : tax}
          className={`${orderSummaryInfo?.orderShippingCharge === 'tbd' ? 'fs-sm lh-sm fst-italic' : ''}`}
        />
        <RenderOrderSummaryLineItem
          label="Total"
          amount={
            orderSummaryInfo?.orderShippingCharge === 'tbd' ? 'tbd' : orderTotal
          }
          labelClassName="fw-bold fs-sm"
          className={`${orderSummaryInfo?.orderShippingCharge === 'tbd' ? 'fst-italic' : 'fw-bold'} fs-sm`}
        />
      </div>
    );
  };

  /**
   * RenderBopisOrderSummary
   */
  const RenderBopisOrderSummary = () => {
    const { amount, tax, orderTotal } = instorePickupSubTotalInfo;
    return (
      <div className="border-top mt-ll-3">
        <RenderOrderSummaryTitle title="Store Pickup" />
        <RenderOrderSummaryLineItem label="Subtotal" amount={amount} />
        <Conditional if={enableTariffSurcharge}>
          <RenderOrderSummaryLineItem
            label="Tariff Recovery Surcharge"
            amount={grandTotalPFormatted(
              instorePickupSubTotalInfo?.tariffAmount,
            )}
            showPopover={true}
            popoverText={orderSummaryInfo?.tariffHelperText}
          />
        </Conditional>

        <RenderOrderSummaryLineItem label="Shipping" amount={'FREE'} />
        <RenderOrderSummaryLineItem label="Taxes" amount={tax} />
        <RenderOrderSummaryLineItem
          label="Total"
          amount={orderTotal}
          labelClassName="fw-bold fs-sm"
          className={`${orderTotal === 'tbd' ? 'fst-italic' : 'fw-bold'} fs-sm`}
        />
      </div>
    );
  };

  /**
   * RenderDigitalOrderSummary
   */
  const RenderDigitalOrderSummary = () => {
    const { amount, tax, orderTotal } = emailDeliverySubtotalInfo;
    return (
      <div className="border-top mt-ll-3">
        <RenderOrderSummaryTitle title="Email Delivery" />
        <RenderOrderSummaryLineItem label="Subtotal" amount={amount} />
        <RenderOrderSummaryLineItem label="Shipping" amount={'FREE'} />
        <RenderOrderSummaryLineItem label="Taxes" amount={tax} />
        <RenderOrderSummaryLineItem
          label="Total"
          amount={orderTotal}
          labelClassName="fw-bold fs-sm"
          className={`${orderTotal === 'tbd' ? 'fst-italic' : 'fw-bold'} fs-sm`}
        />
      </div>
    );
  };

  /**
   * RenderGrandTotalSummary
   */
  const RenderGrandTotalSummary = () => {
    return (
      <div className="border-top border-bottom">
        <Conditional if={orderSummaryInfo?.giftCertificateAmount != '$0.00'}>
          <RenderOrderSummaryLineItem
            label="Gift Card(s)"
            amount={orderSummaryInfo?.giftCertificateAmount}
            addNegative={true}
          />
        </Conditional>
        <Conditional if={orderSummaryInfo?.rewardsPrice != 0}>
          <RenderOrderSummaryLineItem
            label="Lakeshore Bucks"
            amount={orderSummaryInfo?.rewardsPrice}
            addNegative={true}
          />
        </Conditional>
        <Conditional
          if={
            orderSummaryInfo?.discountAmount != 0 &&
            cartSummary.hasPromoApplied &&
            !cartSummary.isShowCouponbox
          }
        >
          <RenderOrderSummaryLineItem
            label="Discount"
            amount={orderSummaryInfo?.discountAmount}
            addNegative={true}
            labelClassName="text-danger"
            className="text-danger"
          />
        </Conditional>
        <div className="d-flex justify-content-between mt-ll-3 mb-ll-5 fs-base lh-base ">
          <div className="fw-bold">Grand Total</div>
          {orderSummaryInfo?.orderShippingCharge === 'tbd' ? (
            <div className="fs-sm lh-sm fst-italic">tbd</div>
          ) : (
            <div className="fw-bold fs-base lh-base">
              {grandTotalPFormatted(orderSummaryInfo?.displayTotal)}
            </div>
          )}
        </div>
      </div>
    );
  };

  /**
   * RenderCustomerServiceDetails
   */
  const RenderCustomerServiceDetails = () => {
    return (
      <div
        className="d-flex justify-content-center align-items-center gap-1 my-ll-5 fs-base lh-base"
        style={{ letterSpacing: '-0.24px' }}
      >
        <span className="fw-bold">Customer Service:</span>

        {/* Show only on mobile (hide on desktops) */}
        <Link
          className="ll-link-txt-style d-block d-md-none"
          href="tel: 8004284414"
        >
          (800) 428-4414
        </Link>
        {/* Show only on desktop (hide on mobile) */}
        <Link className="ll-link-txt-style d-none d-md-block">
          (800) 428-4414
        </Link>
      </div>
    );
  };

  /**
   * RenderOrderSummaryWithoutCategories
   */
  const RenderOrderSummaryWithoutCategories = () => {
    const {
      amount = '',
      tax = '',
      orderTotal = '',
    } = emailDeliverySubtotalInfo ||
    webSubTotalInfo ||
    instorePickupSubTotalInfo ||
    {};

    const totalOrderAmount = isPureOrder
      ? orderSummaryInfo?.displayTotal
      : orderTotal;

    return (
      <div className="mt-ll-3 border-top border-bottom">
        <RenderOrderSummaryLineItem label="Subtotal" amount={amount} />
        <Conditional if={enableTariffSurcharge && !isPureDigitalOrder}>
          <RenderOrderSummaryLineItem
            label="Tariff Recovery Surcharge"
            amount={grandTotalPFormatted(
              orderSummaryInfo?.webSubTotalInfo?.tariffAmount ??
                orderSummaryInfo?.instorePickupSubTotalInfo?.tariffAmount,
            )}
            showPopover={true}
            popoverText={orderSummaryInfo?.tariffHelperText}
          />
        </Conditional>

        <RenderOrderSummaryLineItem
          label="Shipping"
          amount={grandTotalPFormatted(orderSummaryInfo?.orderShippingCharge)}
          className={`${orderSummaryInfo?.orderShippingCharge === 'tbd' || orderSummaryInfo?.orderShippingCharge === 'quote to follow' ? 'fs-sm lh-sm fst-italic' : ''}`}
        />
        <RenderOrderSummaryLineItem
          label="Taxes"
          amount={orderSummaryInfo?.orderShippingCharge === 'tbd' ? 'tbd' : tax}
          className={`${orderSummaryInfo?.orderShippingCharge === 'tbd' ? 'fs-sm lh-sm fst-italic' : ''}`}
        />
        <Conditional if={orderSummaryInfo?.giftCertificateAmount != '$0.00'}>
          <RenderOrderSummaryLineItem
            label="Gift Card(s)"
            amount={orderSummaryInfo?.giftCertificateAmount}
            addNegative={true}
          />
        </Conditional>
        <Conditional if={orderSummaryInfo?.rewardsPrice != 0}>
          <RenderOrderSummaryLineItem
            label="Lakeshore Bucks"
            amount={orderSummaryInfo?.rewardsPrice}
            addNegative={true}
          />
        </Conditional>
        <Conditional
          if={
            orderSummaryInfo?.discountAmount != 0 &&
            cartSummary.hasPromoApplied &&
            !cartSummary.isShowCouponbox
          }
        >
          <RenderOrderSummaryLineItem
            label="Discount"
            amount={orderSummaryInfo?.discountAmount}
            addNegative={true}
            labelClassName="text-danger"
            className="text-danger"
          />
        </Conditional>
        <RenderOrderSummaryLineItem
          label="Total"
          amount={
            orderSummaryInfo?.orderShippingCharge === 'tbd'
              ? 'tbd'
              : grandTotalPFormatted(totalOrderAmount)
          }
          labelClassName="fw-bold fs-base lh-base"
          className={`${orderSummaryInfo?.orderShippingCharge === 'tbd' ? 'fst-italic' : ''} fs-sm lh-base fw-bold`}
        />
      </div>
    );
  };

  return (
    <>
      <Conditional if={!isCheckoutDetailsLoaded}>
        <SkeletonUI width={'100%'} height={30} />
      </Conditional>
      <Conditional if={isCheckoutDetailsLoaded}>
        <>
          <div
            className={`d-lg-none mb-ll-3 mt-ll-5 ${showHeading ? '' : 'd-none'}`}
          >
            <LLHeading tagName="2" className="7">
              Order Summary
            </LLHeading>
          </div>
          <Conditional if={isPureOrder}>
            <>
              <RenderOrderSummaryWithoutCategories />
            </>
          </Conditional>
          <Conditional if={!isPureOrder}>
            <>
              <Conditional if={isNotEmptyObject(webSubTotalInfo)}>
                <RenderShipOrderSummary />
              </Conditional>
              <Conditional if={isNotEmptyObject(instorePickupSubTotalInfo)}>
                <RenderBopisOrderSummary />
              </Conditional>
              <Conditional if={isNotEmptyObject(emailDeliverySubtotalInfo)}>
                <RenderDigitalOrderSummary />
              </Conditional>

              <RenderGrandTotalSummary />
            </>
          </Conditional>
          <Conditional if={taxExemptOpted}>
            <TaxExempt amount={orderSummaryInfo?.displayTotal} />
          </Conditional>
          <RenderCustomerServiceDetails />
        </>
      </Conditional>
    </>
  );
};
export default OrderSummary;
